import { Eye, PlusIcon, TrashIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCradSize } from "@/hooks/use-card-size";
import { TruncatedText } from "@/components/common/truncated-text";
import { IconButton } from "@/components/common/icon-button";
import { Image } from "@/components/common/image";
import { DeleteConfirmModal, Tooltip } from "@/components/ui/modal";
import macosFolderBlack from '@/assets/agent-user-detail/macos-folder-black@3x.webp';
import { useEffect, useState } from "react";
import { AddMaterialIcon, MaterialEmptyImage } from "./assets";
import { MaterialModalDetail } from "@/components/material-modal-detail";
import { MaterialModalAdd } from "@/components/material-model-add";
import { agentDashboardMaterialHooks } from '@/manager'
import { LoadingOverlay } from "../common/loading-overlay";
import { useParams } from "react-router-dom";

interface MaterialLibraryProps {
  activeTab?: string;
}

/** 素材库 */
export const MaterialLibrary = ({ activeTab }: MaterialLibraryProps) => {
  const { t } = useTranslation();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectId, setSelectId] = useState<number>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailFolderName, setDetailFolderName] = useState<string>(null);

  // 智能体用户素材库数据
  const { agentUserMaterials, loading, searchAgentUserMaterials } = agentDashboardMaterialHooks.useAgentUserMaterialData();
  // 素材库目录内文件列表数据
  const { materialFolderItems, loading: materialFolderItemsLoading, fetchMaterialFolderItems } = agentDashboardMaterialHooks.useAgentUserMaterialItems();
  // 素材库添加弹窗列表数据
  const { materialFolders, loading: materialFoldersLoading } = agentDashboardMaterialHooks.useMaterialFoldersData();
  // 创建智能体用户素材库
  const { agentUserMaterialCreate, loading: agentUserMaterialCreateLoading } = agentDashboardMaterialHooks.useAgentUserMaterialCreate();
  // 删除智能体用户素材库
  const { agentUserMaterialDelete, loading: agentUserMaterialDeleteLoading } = agentDashboardMaterialHooks.useAgentUserMaterialDelete();

  const { ref, columns, width, height } = useCradSize({
    min: 170,
    max: 220,
    gap: 3,
  });

  useEffect(() => {
    searchAgentUserMaterials();
  }, [activeTab])

  const handleDelete = (id: number) => {
    setDeleteModalOpen(true);
    setSelectId(id);
  }

  const confirmDelete = async () => {
    await agentUserMaterialDelete(selectId);
    setDeleteModalOpen(false);
  }

  const handleCreate = async (folderIds: number[]) => {
    try {
      await agentUserMaterialCreate(folderIds);
    } catch (error) {
      console.log(error);
    }
  }

  const handleDetail = async (folderId: number, folderName: string) => {
    fetchMaterialFolderItems(folderId);
    setDetailModalOpen(true);
    setDetailFolderName(folderName);
  }

  return (
    <div className="w-full h-full flex flex-col relative mb-20">
      <div className='flex items-center justify-between text-base font-medium text-[rgba(0,0,0,1)] mb-5'>
        <span>{t("agent_dashboard.user_detail.material.title")}</span>
        <MaterialModalAdd
          trigger={
            <button className='h-10 flex items-center justify-center py-[10px] px-[14px] border border-[rgba(213,215,218,1)] hover:bg-gray-50  rounded-md shadow-sm transition-all'>
              <AddMaterialIcon />
              <span className='ml-1.5 text-sm text-[rgba(16,24,40,1)] font-semibold'>{t("agent_dashboard.user_detail.material.add_asset")}</span>
            </button>
          }
          materialFolders={materialFolders}
          loading={agentUserMaterialCreateLoading}
          onSave={handleCreate}
        />
      </div>
      <div
        ref={ref}
        className='grid gap-x-2 gap-y-18'
        style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
      >
        {loading && <MaterialSkeleton />}
        {!loading && agentUserMaterials.length ? (
          agentUserMaterials.map((item, index) => (
            <div key={item.id} className="w-full h-full relative">
              <div className="group relative w-full rounded-[12px] mb-[10px] transition-all cursor-pointer select-none">
                <div className='overflow-hidden flex-shrink-0'
                  onClick={() => handleDetail(item.folderId, item.folderName)}
                >
                  <Image src={macosFolderBlack} className="size-full object-cover" />
                </div>
                <div className='opacity-0 group-hover:opacity-100 absolute bottom-[14px] right-[14px] transition-all flex gap-1 bg-[rgba(0,0,0,0.4)] rounded-[8px] px-1 py-1'>
                  <Tooltip
                    side='top'
                    align='center'
                    trigger={
                      <IconButton
                        className="hover:bg-[rgba(255,255,255,0.2)]"
                        icon={<Eye className="w-4 h-4 text-white" />}
                        onClick={() => handleDetail(item.folderId, item.folderName)}
                      />
                    }
                    content={
                      <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
                        <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                          {t("agent_dashboard.user_detail.material.view_detail")}
                        </span>
                        <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                      </div>
                    }
                  />
                  <Tooltip
                    side='top'
                    align='center'
                    trigger={
                      <IconButton
                        className="hover:bg-[rgba(255,255,255,0.2)]"
                        icon={<TrashIcon className="w-4 h-4 text-white" />}
                        onClick={() => handleDelete(item.folderId)}
                      />
                    }
                    content={
                      <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2  rounded-[8px]'>
                        <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                          {t("agent_dashboard.user_detail.material.delete")}
                        </span>
                        <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                      </div>
                    }
                  />
                </div>
              </div>
              <div className="absolute bottom-1 left-[10px] w-full translate-y-full">
                <div className="w-full text-center">
                  <TruncatedText text={item.folderName} className='max-w-[90%] text-base font-medium text-[rgba(16,24,40,1)]' />
                </div>
                <div className="w-full text-center mt-1">
                  <TruncatedText text={`${item.itemCount}项`} className='max-w-[90%] text-sm text-[rgba(102,112,133,1)]' />
                </div>
              </div>
            </div>
          ))
        ) : !loading && (
          <div className=' w-full h-full relative  flex flex-col justify-center items-center col-span-full mt-3 mb-16'>
            <MaterialEmptyImage className='w-[159px] h-[136px]' />
            <span className='text-base text-[rgba(102,112,133,1)]'>{t("agent_dashboard.user_detail.material.material_empty")}</span>
          </div>
        )}
      </div>
      <DeleteConfirmModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onConfirm={confirmDelete}
        loading={agentUserMaterialDeleteLoading}
      />
      <MaterialModalDetail
        name={detailFolderName}
        open={detailModalOpen}
        loading={materialFolderItemsLoading}
        materialFolderItems={materialFolderItems}
        onOpenChange={setDetailModalOpen}
      />
    </div>
  )
}

// 骨架屏组件
function MaterialSkeleton() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, index) => (
        <div className="w-full h-full relative animate-pulse">
          <div className="w-full rounded-[12px] mb-[10px] bg-gray-200 aspect-square"></div>
          <div className="absolute bottom-1 w-full translate-y-full">
            <div className="w-full text-center">
              <div className="max-w-[70%] mx-auto h-5 bg-gray-200 rounded"></div>
            </div>
            <div className="w-full text-center mt-1">
              <div className="max-w-[20%] mx-auto h-3 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}